import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const getVariablesFromBody = (body) => {
  if (!body) return [];
  const re = /\{\{(\d+)\}\}/g;
  const seen = new Set();
  const vars = [];
  let m;
  while ((m = re.exec(body)) !== null) {
    const num = m[1];
    if (!seen.has(num)) {
      seen.add(num);
      vars.push({ var: `{{${num}}}`, description: '' });
    }
  }
  return vars.sort((a, b) => parseInt(a.var.replace(/\D/g, ''), 10) - parseInt(b.var.replace(/\D/g, ''), 10));
};

const MessageTemplates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'MARKETING',
    language: 'en_US',
    body_text: '',
    header_text: '',
    footer_text: '',
    variable_descriptions: [],
    example_values: [],
    button_config: { buttons: [] },
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const res = await api.get('/whatsapp/masters/', { params });
      setTemplates(res.data.templates || []);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Failed to load templates', severity: 'error' });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleOpenAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      category: 'MARKETING',
      language: 'en_US',
      body_text: '',
      header_text: '',
      footer_text: '',
      variable_descriptions: [],
      example_values: [],
      button_config: { buttons: [] },
    });
    setAddTemplateOpen(true);
  };

  const handleCloseAddTemplate = () => {
    setAddTemplateOpen(false);
    setEditingTemplate(null);
  };

  const variableDescriptionsFromBody = useMemo(() => {
    const vars = getVariablesFromBody(templateForm.body_text);
    const existing = (templateForm.variable_descriptions || []).slice();
    const byVar = {};
    existing.forEach((e) => { byVar[e.var] = e.description; });
    return vars.map((v) => ({ var: v.var, description: byVar[v.var] ?? v.description ?? '' }));
  }, [templateForm.body_text, templateForm.variable_descriptions]);

  const handleTemplateFormChange = (field) => (e) => {
    const value = e.target.value;
    setTemplateForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'body_text') {
        const vars = getVariablesFromBody(value);
        const byVar = {};
        (prev.variable_descriptions || []).forEach((e) => { byVar[e.var] = e.description; });
        next.variable_descriptions = vars.map((v) => ({ var: v.var, description: byVar[v.var] ?? '' }));
        next.example_values = vars.map((v, i) => prev.example_values?.[i] ?? '');
      }
      return next;
    });
  };

  const setVariableDescription = (index, description) => {
    setTemplateForm((prev) => {
      const vars = getVariablesFromBody(prev.body_text);
      const byVar = {};
      (prev.variable_descriptions || []).forEach((e) => { byVar[e.var] = e.description; });
      const list = vars.map((v) => ({ var: v.var, description: byVar[v.var] ?? '' }));
      if (list[index]) list[index] = { ...list[index], description };
      return { ...prev, variable_descriptions: list };
    });
  };

  const setExampleValue = (index, value) => {
    setTemplateForm((prev) => {
      const list = [...(prev.example_values || [])];
      list[index] = value;
      return { ...prev, example_values: list };
    });
  };

  const buildPayload = () => {
    const variable_descriptions = variableDescriptionsFromBody;
    const example_values = variable_descriptions.map((_, i) => templateForm.example_values?.[i] ?? '');
    const buttons = (templateForm.button_config?.buttons || [])
      .filter((b) => b && (b.text || '').trim())
      .map((b) => ({ type: 'QUICK_REPLY', text: (b.text || '').trim() }));
    return {
      name: templateForm.name.trim().toLowerCase().replace(/\s+/g, '_'),
      category: templateForm.category,
      language: templateForm.language,
      header_text: templateForm.header_text?.trim() || null,
      body_text: templateForm.body_text?.trim(),
      footer_text: templateForm.footer_text?.trim() || null,
      variable_descriptions,
      example_values,
      button_config: buttons.length ? { buttons } : {},
    };
  };

  const buttonList = templateForm.button_config?.buttons || [];
  const maxButtons = 3;

  const addButton = () => {
    if (buttonList.length >= maxButtons) return;
    setTemplateForm((prev) => ({
      ...prev,
      button_config: {
        buttons: [...(prev.button_config?.buttons || []), { type: 'QUICK_REPLY', text: '' }],
      },
    }));
  };

  const updateButton = (index, text) => {
    setTemplateForm((prev) => {
      const list = [...(prev.button_config?.buttons || [])];
      if (list[index]) list[index] = { ...list[index], type: 'QUICK_REPLY', text };
      return { ...prev, button_config: { buttons: list } };
    });
  };

  const removeButton = (index) => {
    setTemplateForm((prev) => {
      const list = (prev.button_config?.buttons || []).filter((_, i) => i !== index);
      return { ...prev, button_config: { buttons: list } };
    });
  };

  const handleSaveTemplate = async () => {
    const payload = buildPayload();
    if (!payload.name || !payload.body_text) {
      setSnackbar({ open: true, message: 'Name and body are required', severity: 'error' });
      return;
    }
    try {
      if (editingTemplate) {
        await api.patch(`/whatsapp/masters/${editingTemplate.id}/`, payload);
        setSnackbar({ open: true, message: 'Template updated', severity: 'success' });
      } else {
        await api.post('/whatsapp/masters/', payload);
        setSnackbar({ open: true, message: 'Template created', severity: 'success' });
      }
      handleCloseAddTemplate();
      fetchTemplates();
    } catch (e) {
      const msg = e.response?.data?.error || (typeof e.response?.data === 'object' ? JSON.stringify(e.response?.data) : 'Failed to save');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleEditTemplate = (row) => {
    setEditingTemplate(row);
    const buttons = (row.button_config?.buttons || []).map((b) =>
      typeof b === 'string' ? { type: 'QUICK_REPLY', text: b } : { type: b.type || 'QUICK_REPLY', text: b.text || '' }
    );
    setTemplateForm({
      name: row.name,
      category: row.category,
      language: row.language,
      body_text: row.body_text || '',
      header_text: row.header_text || '',
      footer_text: row.footer_text || '',
      variable_descriptions: row.variable_descriptions || [],
      example_values: row.example_values || [],
      button_config: { buttons },
    });
    setAddTemplateOpen(true);
  };

  const handleDeleteTemplate = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    try {
      await api.delete(`/whatsapp/masters/${row.id}/`);
      setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
      fetchTemplates();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Delete failed', severity: 'error' });
    }
  };

  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return templates;
    const s = searchTerm.toLowerCase();
    return templates.filter(
      (t) =>
        (t.name && t.name.toLowerCase().includes(s)) ||
        (t.body_text && t.body_text.toLowerCase().includes(s))
    );
  }, [templates, searchTerm]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Masters
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Message templates for WhatsApp
      </Typography>

      <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
              <TextField
                size="small"
                placeholder="Search by template name or..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTemplates()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTemplates} disabled={loading}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }} onClick={handleOpenAddTemplate}>
                Add Message Template
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No message templates yet. Click &quot;Add Message Template&quot; to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTemplates.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {row.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={row.category || row.category_display} sx={{ bgcolor: 'grey.200' }} />
                          </TableCell>
                          <TableCell>{row.language}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditTemplate(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(row)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filteredTemplates.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Template preview (variables)</Typography>
                {filteredTemplates.map((t) => (
                  <Card key={t.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t.name}</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{t.body_text}</Typography>
                    {(t.variable_descriptions || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Variables:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {t.variable_descriptions.map((vd, i) => (
                            <Chip
                              key={i}
                              size="small"
                              label={`${vd.var}: ${vd.description || '—'}`}
                              sx={{ bgcolor: 'success.50', color: 'success.dark' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {(t.button_config?.buttons || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Buttons:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {t.button_config.buttons.map((b, i) => (
                            <Chip
                              key={i}
                              size="small"
                              label={b.text || `Button ${i + 1}`}
                              sx={{ bgcolor: 'success.50', color: 'success.dark' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Chip size="small" label={t.category} sx={{ mt: 1, bgcolor: 'grey.200' }} />
                  </Card>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

      <Dialog open={addTemplateOpen} onClose={handleCloseAddTemplate} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Message Template' : 'Add Message Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Template name"
              value={templateForm.name}
              onChange={handleTemplateFormChange('name')}
              placeholder="e.g. new_lead_intro_with_requirements"
              fullWidth
              required
              size="small"
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Category</InputLabel>
              <Select
                value={templateForm.category}
                label="Category"
                onChange={(e) => setTemplateForm((p) => ({ ...p, category: e.target.value }))}
              >
                <MenuItem value="MARKETING">Marketing</MenuItem>
                <MenuItem value="UTILITY">Utility</MenuItem>
                <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Language</InputLabel>
              <Select
                value={templateForm.language}
                label="Language"
                onChange={(e) => setTemplateForm((p) => ({ ...p, language: e.target.value }))}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="en_US">English (US)</MenuItem>
                <MenuItem value="en_IN">English (India)</MenuItem>
                <MenuItem value="hi">Hindi</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Header (optional)"
              value={templateForm.header_text || ''}
              onChange={handleTemplateFormChange('header_text')}
              placeholder="Template header text"
              fullWidth
              size="small"
              multiline
              rows={1}
            />
            <TextField
              label="Body"
              value={templateForm.body_text}
              onChange={handleTemplateFormChange('body_text')}
              placeholder="e.g. Hi {{1}}, this is {{2}} from {{3}} regarding your enquiry for {{4}}."
              fullWidth
              required
              size="small"
              multiline
              rows={4}
            />
            {variableDescriptionsFromBody.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Variable descriptions</Typography>
                {variableDescriptionsFromBody.map((vd, i) => (
                  <TextField
                    key={vd.var}
                    label={`${vd.var}`}
                    value={vd.description}
                    onChange={(e) => setVariableDescription(i, e.target.value)}
                    placeholder="e.g. Customer name"
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                  />
                ))}
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Example values (optional)</Typography>
                {variableDescriptionsFromBody.map((vd, i) => (
                  <TextField
                    key={`ex-${vd.var}`}
                    label={`Example for ${vd.var}`}
                    value={templateForm.example_values?.[i] ?? ''}
                    onChange={(e) => setExampleValue(i, e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            )}
            <TextField
              label="Footer (optional)"
              value={templateForm.footer_text || ''}
              onChange={handleTemplateFormChange('footer_text')}
              placeholder="Template footer text"
              fullWidth
              size="small"
              multiline
              rows={1}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Buttons (optional, max 3 quick reply)
              </Typography>
              {buttonList.map((btn, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    label={`Button ${i + 1}`}
                    value={btn.text || ''}
                    onChange={(e) => updateButton(i, e.target.value)}
                    placeholder="e.g. Interested"
                    fullWidth
                    size="small"
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeButton(i)}
                    sx={{ minWidth: 36 }}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              {buttonList.length < maxButtons && (
                <Button size="small" variant="outlined" onClick={addButton} sx={{ mt: 0.5 }}>
                  Add quick reply button
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddTemplate}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={!templateForm.name?.trim() || !templateForm.body_text?.trim()}
          >
            {editingTemplate ? 'Update Template' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageTemplates;
